/** @jsx jsx */
// eslint-disable-next-line
import React from 'react'
import PropTypes from 'prop-types'
import { css, jsx } from '@emotion/core'
import {
  Button,
  CircularProgress,
  ClickAwayListener,
  Grow,
  Icon,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from '@material-ui/core'
import { theme } from '../../../constants'
import AddOrEditOrganizationModal from './AddOrEditOrganizationModal'
import ManageOrganizationMinutesModal from './ManageOrganizationMinutesModal'
import DeleteOrganizationModal from './DeleteOrganizationModal'
import { history } from '../../App'

const styles = {}
styles.loadingContainer = css`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: white;
  opacity: 0.5;
  z-index: 1;
`

styles.container = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: column;
  margin: auto;
`

styles.headerContainer = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: start;
  margin-top: 10px;
  margin-bottom: 20px;
`

styles.subHeaderContainer = css`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`

styles.inputContainer = css`
  display: flex;
  align-items: center;
  width: calc(100% / 3);
`

styles.descriptionContainer = css`
  padding-left: 16px;
  font-size: 14px;
`

styles.headerIcon = { marginRight: '6px', color: 'white' }

styles.inputSearchContainer = css`
  display: flex;
  width: 100%;
`

styles.inputSearch = css`
  color: white;
  background: transparent;
  border: none;
  outline: 0;
  font-size: 16px;
  width: 100%;
  font-color: white;
  font-weight: 500;
  opacity: 1;
`

styles.menuHeader = {
  zIndex: '9',
  position: 'relative'
}

styles.buttonHeader = {
  height: '30px',
  padding: '16px 10px',
  marginLeft: '10px',
  textTransform: 'none',
  fontWeight: 'none',
  lineHeight: 'inherit'
}

styles.buttonUpgrade = {
  ...styles.buttonHeader,
  color: 'white',
  background: theme.palette.primary.main
}

styles.buttonGroups = {
  ...styles.buttonHeader,
  color: 'white',
  background: theme.palette.primary.main
}

styles.buttonMinutes = {
  ...styles.buttonHeader,
  color: 'white',
  background: '#8800ff'
}

styles.tableIcon = {
  color: 'white',
  fontSize: '20px',
  justifyContent: 'right'
}

styles.tableHeader = css`
  font-size: 18px;
  text-align: left;
  border-bottom: 1px solid white;
  cursor: pointer;
`

styles.tableSortIcon = css`
  font-size: 9px;
  margin-left: 5px;
  position: relative;
  top: -2px;
`

styles.tableCell = css`
  font-size: 12px;
  padding: 12px 0;
  border-bottom: 1px solid white;
`

function Organizations(props) {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null)
  const [searchValue, setSearchValue] = React.useState('')
  const [hasPermission, setHasPermission] = React.useState(false)
  const [ownerOrganization, setOwnerOrganization] = React.useState(null)
  const [groupOrganizations, setGroupOrganizations] = React.useState([])
  const [filteredOrganizations, setFilteredOrganizations] = React.useState([])
  const [manageMinutesModal, setManageMinutesModal] = React.useState(false)
  const [addOrganizationModal, setAddOrganizationModal] = React.useState(false)
  const [editOrganization, setEditOrganization] = React.useState(null)
  const [deleteOrganizationModal, setDeleteOrganizationModal] = React.useState(null)
  const [moreOptionsOrganization, setMoreOptionsOrganization] = React.useState(null)
  const [sortOrderAsc, setSortOrderAsc] = React.useState(true)
  const [sortByField, setSortByField] = React.useState('name')

  React.useEffect(() => {
    setOwnerOrganization(props.organizations[0])
    setGroupOrganizations(
      props.organizations.slice(1).map(groupOrganization => {
        const nUsersInOrganization = props.organizations[0].users.reduce((acc, v) => {
          const belongs = v.organizations.some(w => w.organization._id === groupOrganization._id)
          return acc + (belongs ? 1 : 0)
        }, 0)

        return {
          ...groupOrganization,
          minutes: Math.floor(groupOrganization.tracking_minutes * 100) / 100,
          users: nUsersInOrganization
        }
      })
    )

    setHasPermission(
      props.organizations[0].users.some(
        v => v.email === props.user.email && (v.role === 'owner' || v.role === 'admin')
      )
    )
  }, [props.organizations])

  React.useEffect(() => {
    if (searchValue) {
      const filtered = groupOrganizations.filter(organization => {
        const organizationName = organization.name.toLowerCase()
        const search = searchValue.trim().toLowerCase()

        return organizationName.includes(search)
      })

      setFilteredOrganizations(filtered)
    } else {
      setFilteredOrganizations(groupOrganizations)
    }
  }, [searchValue, groupOrganizations])

  const sortBy = field => {
    if (field === sortByField) {
      setSortOrderAsc(!sortOrderAsc)
    } else {
      setSortByField(field)
      setSortOrderAsc(true)
    }
  }

  const onManageMinutes = () => {
    setManageMinutesModal(true)
  }

  const onAddOrganization = () => {
    setAddOrganizationModal(true)
  }

  const onEditOrganization = organization => {
    setEditOrganization(organization)
  }

  const onMoreOptionsOrganization = (organization, anchorEl) => {
    setMenuAnchorEl(anchorEl)
    setMoreOptionsOrganization(organization)
  }

  const onMoreOptionsOrganizationClose = () => {
    setMoreOptionsOrganization(null)
    setMenuAnchorEl(null)
  }

  const onDeleteOrganization = organization => {
    setDeleteOrganizationModal(organization)
  }

  const onMinutesManaged = () => {
    setManageMinutesModal(false)
  }

  const onOrganizationAdded = organization => {
    setAddOrganizationModal(false)
  }

  const onOrganizationEdited = organization => {
    setEditOrganization(null)
  }

  const onOrganizationDeleted = () => {
    setDeleteOrganizationModal(null)
  }

  if (!ownerOrganization) {
    return (
      <div css={styles.loadingContainer}>
        <CircularProgress size={32} thickness={4} />
      </div>
    )
  }

  if (!hasPermission) {
    return (
      <div
        css={styles.descriptionContainer}
        dangerouslySetInnerHTML={{
          __html: window.I18n.t('groups.noPermissionDescription', {
            ownerEmail: ownerOrganization.owner_email,
            pre: '<b>',
            post: '</b>'
          })
        }}
      />
    )
  }

  // An order can have several plans, each with its own user-key. Skip order items without a base
  // template (e.g. one-time purchases), they are not licenses.
  const nLicenses = props.subscriptions.reduce((acc, v) => {
    let userKeys = [v.userKey]
    if (v.order) {
      userKeys = v.order.items
        .map(item => item.user_key)
        .filter(w => w && w.licence_configuration && w.licence_configuration.base_template)
    }

    return acc + userKeys.reduce((sum, userKey) => sum + (userKey ? userKey.max_usage : 0), 0)
  }, 0)

  const sortedAndFilteredOrganizations = [...filteredOrganizations].sort((a, b) => {
    let aValue = ''
    let bValue = ''

    if (sortByField === 'name') {
      aValue = a.name.toLowerCase()
      bValue = b.name.toLowerCase()
    } else if (sortByField === 'minutes') {
      aValue = a.minutes
      bValue = b.minutes
    } else if (sortByField === 'users') {
      aValue = a.users
      bValue = b.users
    }

    if (aValue < bValue) {
      return sortOrderAsc ? -1 : 1
    }
    if (aValue > bValue) {
      return sortOrderAsc ? 1 : -1
    }
    return 0
  })

  return (
    <React.Fragment>
      {manageMinutesModal && (
        <ManageOrganizationMinutesModal
          user={props.user}
          ownerOrganization={ownerOrganization}
          organizations={props.organizations}
          onApply={onMinutesManaged}
        />
      )}

      {addOrganizationModal && (
        <AddOrEditOrganizationModal
          user={props.user}
          ownerOrganization={ownerOrganization}
          onApply={onOrganizationAdded}
        />
      )}

      {editOrganization && (
        <AddOrEditOrganizationModal
          user={props.user}
          organization={editOrganization}
          ownerOrganization={ownerOrganization}
          onApply={onOrganizationEdited}
        />
      )}

      {deleteOrganizationModal && (
        <DeleteOrganizationModal
          organization={deleteOrganizationModal}
          ownerOrganization={ownerOrganization}
          onApply={onOrganizationDeleted}
        />
      )}

      <Popper
        open={moreOptionsOrganization}
        anchorEl={menuAnchorEl}
        style={styles.menuHeader}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            id="menu-list-grow"
            style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
          >
            <Paper>
              <ClickAwayListener onClickAway={onMoreOptionsOrganizationClose}>
                <MenuList>
                  <MenuItem
                    style={styles.menuItem}
                    onClick={() => {
                      onEditOrganization(moreOptionsOrganization)
                    }}
                  >
                    {window.I18n.t('groups.addMembers')}
                  </MenuItem>
                  <MenuItem
                    style={{ ...styles.menuItem, color: 'red' }}
                    onClick={() => {
                      onDeleteOrganization(moreOptionsOrganization)
                    }}
                  >
                    {window.I18n.t('groups.deleteThisGroup')}
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <div css={styles.container}>
        <div css={styles.subHeaderContainer}>
          <div css={styles.inputContainer}>
            <Icon style={styles.headerIcon}>search</Icon>
            <div
              css={styles.inputSearchContainer}
              style={{ borderBottom: searchValue ? '1px solid white' : '' }}
            >
              <input
                type="text"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
                css={styles.inputSearch}
                placeholder={window.I18n.t('admin.search')}
              />
              {!!searchValue && (
                <Icon style={styles.closeIcon} onClick={() => setSearchValue('')}>
                  highlight_off
                </Icon>
              )}
            </div>
          </div>

          <div>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{
                ...styles.buttonMinutes,
                ...(nLicenses <= 1 && filteredOrganizations.length === 0 ? { opacity: 0.5 } : {})
              }}
              disabled={nLicenses <= 1 && filteredOrganizations.length === 0}
              onClick={onManageMinutes}
            >
              <Icon style={styles.headerIcon}>create</Icon>
              <span>{window.I18n.t('groups.manageGameCloudMinutes')}</span>
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              style={{ ...styles.buttonGroups, ...(nLicenses <= 1 ? { opacity: 0.5 } : {}) }}
              disabled={nLicenses <= 1}
              onClick={onAddOrganization}
            >
              <Icon style={styles.headerIcon}>add</Icon>
              <span>{window.I18n.t('groups.createGroup')}</span>
            </Button>
          </div>
        </div>

        {/* Users table */}
        <div style={{ width: '100%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th
                  css={styles.tableHeader}
                  style={{ width: '40%' }}
                  onClick={() => sortBy('name')}
                >
                  {window.I18n.t('groups.name')}
                  <span css={styles.tableSortIcon}>
                    {sortByField === 'name' && (sortOrderAsc ? ' ▲' : ' ▼')}
                  </span>
                </th>
                <th
                  css={styles.tableHeader}
                  style={{ width: '25%' }}
                  onClick={() => sortBy('minutes')}
                >
                  {window.I18n.t('groups.gameCloudMinutes')}
                  <span css={styles.tableSortIcon}>
                    {sortByField === 'minutes' && (sortOrderAsc ? ' ▲' : ' ▼')}
                  </span>
                </th>
                <th
                  css={styles.tableHeader}
                  style={{ width: '25%' }}
                  onClick={() => sortBy('users')}
                >
                  {window.I18n.t('groups.groupMembers')}
                  <span css={styles.tableSortIcon}>
                    {sortByField === 'users' && (sortOrderAsc ? ' ▲' : ' ▼')}
                  </span>
                </th>
                <th
                  css={styles.tableHeader}
                  style={{ width: '10%', textAlign: 'right', paddingRight: '8px' }}
                />
              </tr>
            </thead>
            <tbody>
              {nLicenses <= 1 && groupOrganizations.length === 0 && (
                <tr>
                  <td colSpan="4" css={styles.tableCell}>
                    {window.I18n.t('groups.noMultiLicenseDescription')}
                  </td>
                </tr>
              )}
              {nLicenses > 1 && sortedAndFilteredOrganizations.length === 0 && (
                <tr>
                  <td colSpan="4" css={styles.tableCell}>
                    {window.I18n.t('admin.NoResults')}
                  </td>
                </tr>
              )}
              {sortedAndFilteredOrganizations.map(v => {
                let anchorEl = null
                return (
                  <tr key={v.code}>
                    <td css={styles.tableCell}>{v.name}</td>
                    <td css={styles.tableCell}>{v.minutes}</td>
                    <td css={styles.tableCell}>{v.users}</td>
                    <td css={styles.tableCell} style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex' }}>
                        <Button
                          aria-haspopup="true"
                          style={styles.tableIcon}
                          onClick={() => onEditOrganization(v)}
                          buttonRef={node => {
                            anchorEl = node
                          }}
                          aria-owns={moreOptionsOrganization ? 'menu-list-grow' : undefined}
                        >
                          <Icon>edit</Icon>
                        </Button>
                        <Button
                          aria-haspopup="true"
                          style={{ ...styles.tableIcon, paddingRight: '24px' }}
                          onClick={() => onMoreOptionsOrganization(v, anchorEl)}
                          buttonRef={node => {
                            anchorEl = node
                          }}
                          aria-owns={moreOptionsOrganization ? 'menu-list-grow' : undefined}
                        >
                          <Icon>more_vert</Icon>
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </React.Fragment>
  )
}

Organizations.defaultProps = {}

Organizations.propTypes = {
  user: PropTypes.object.isRequired,
  organizations: PropTypes.array.isRequired,
  subscriptions: PropTypes.array.isRequired
}

export default Organizations
